import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';

// Usar o mesmo domínio que a página está sendo servida (funciona em produção e preview)
const API = typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api';
const BACKEND_URL = typeof window !== 'undefined' ? window.location.origin : '';

// Helper para URL de imagens
const getImageUrl = (image) => {
  if (!image) return null;
  if (image.startsWith('data:image')) return image;
  if (image.startsWith('http')) return image;
  if (image.startsWith('/uploads')) return `${BACKEND_URL}/api${image}`;
  return `data:image/jpeg;base64,${image}`;
};

const AdminDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [financial, setFinancial] = useState(null);
  const [activeCard, setActiveCard] = useState(null);
  const [candidatosStats, setCandidatosStats] = useState(null);

  useEffect(() => {
    // Esperar o auth carregar
    if (authLoading) return;
    
    if (!user || user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadDashboardData();
  }, [user, authLoading]);

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, financialRes, candidatosRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard/stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/dashboard/financial`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/candidatos/stats`, { headers }).catch(() => ({ data: null }))
      ]);
      
      setStats(statsRes.data);
      setFinancial(financialRes.data);
      setCandidatosStats(candidatosRes.data);
    } catch (error) {
      console.error('Dashboard error:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // KPI Cards Data
  const kpiCards = [
    // Indicadores em tempo real
    {
      id: 'online',
      title: '🟢 Usuários Online',
      value: stats?.realtime?.online_total || 0,
      icon: '🟢',
      color: 'from-green-500 to-green-700',
      subtitle: `${stats?.realtime?.online_creators || 0} criadores • ${stats?.realtime?.online_viewers || 0} fãs online`,
      pulse: true,
      onClick: () => navigate('/admin/online-users')
    },
    {
      id: 'visitas',
      title: '📊 Visitas Hoje',
      value: stats?.realtime?.visits_today || 0,
      icon: '📊',
      color: 'from-blue-500 to-blue-700',
      subtitle: `${stats?.realtime?.new_users_today || 0} novos cadastros`,
      pulse: false,
      onClick: () => navigate('/admin/analytics')
    },
    {
      id: 'receita',
      title: 'Receita Total',
      value: financial?.summary?.total_revenue ? `R$ ${financial.summary.total_revenue.toFixed(2)}` : 'R$ 0,00',
      icon: '💰',
      color: 'from-[#C79F5E] to-[#8B7355]',
      subtitle: `${stats?.transactions?.successful || 0} transações`,
      onClick: () => setActiveCard(activeCard === 'receita' ? null : 'receita')
    },
    {
      id: 'plataforma',
      title: 'Receita LYBERHOT',
      value: financial?.summary?.platform_revenue ? `R$ ${financial.summary.platform_revenue.toFixed(2)}` : 'R$ 0,00',
      icon: '🏢',
      color: 'from-[#4D1A4A] to-[#6B2D5C]',
      subtitle: 'Comissão 15%',
      onClick: () => setActiveCard(activeCard === 'plataforma' ? null : 'plataforma')
    },
    {
      id: 'criadores',
      title: 'Pago aos Criadores',
      value: financial?.summary?.creators_revenue ? `R$ ${financial.summary.creators_revenue.toFixed(2)}` : 'R$ 0,00',
      icon: '👨‍🎨',
      color: 'from-green-600 to-green-800',
      subtitle: '85% da receita',
      onClick: () => setActiveCard(activeCard === 'criadores' ? null : 'criadores')
    },
    {
      id: 'usuarios',
      title: 'Total Usuários',
      value: stats?.users?.total || 0,
      icon: '👥',
      color: 'from-blue-600 to-blue-800',
      subtitle: `${stats?.users?.fundadores || 0} fundadores • ${stats?.users?.criadores || 0} criadores • ${stats?.users?.fans || 0} fãs`,
      onClick: () => navigate('/admin/creators')
    },
    {
      id: 'fundadores',
      title: '💎 Fundadores',
      value: stats?.users?.fundadores || 0,
      icon: '💎',
      color: 'from-[#D4AF37] to-[#A67C3D]',
      subtitle: 'Taxa: 85% / 15%',
      onClick: () => setActiveCard(activeCard === 'fundadores' ? null : 'fundadores')
    },
    {
      id: 'criadores_card',
      title: '⭐ Criadores',
      value: stats?.users?.criadores || 0,
      icon: '⭐',
      color: 'from-[#C79F5E] to-[#8B7355]',
      subtitle: 'Taxa: 82% / 18%',
      onClick: () => navigate('/admin/creators')
    },
    {
      id: 'candidatos',
      title: 'Candidatos Pendentes',
      value: candidatosStats?.pendentes || 0,
      icon: '📋',
      color: 'from-yellow-600 to-orange-600',
      subtitle: `${candidatosStats?.total || 0} total`,
      onClick: () => navigate('/admin/candidatos')
    },
    {
      id: 'assinaturas',
      title: 'Assinaturas Ativas',
      value: stats?.subscriptions?.active || 0,
      icon: '🔄',
      color: 'from-purple-600 to-purple-800',
      subtitle: `${stats?.subscriptions?.total || 0} total`,
      onClick: () => setActiveCard(activeCard === 'assinaturas' ? null : 'assinaturas')
    },
    {
      id: 'posts',
      title: 'Posts Publicados',
      value: stats?.content?.total_posts || 0,
      icon: '📱',
      color: 'from-pink-600 to-pink-800',
      subtitle: `${stats?.content?.premium_posts || 0} premium`,
      onClick: () => setActiveCard(activeCard === 'posts' ? null : 'posts')
    }
  ];

  // Quick Actions
  const quickActions = [
    { label: '📋 Triagem Candidatos', path: '/admin/candidatos', color: 'bg-gradient-to-r from-[#D4AF37] to-[#8B7355] text-black' },
    { label: '👥 Gerenciar Criadores', path: '/admin/creators', color: 'bg-gradient-to-r from-[#C79F5E] to-[#A67C3D] text-black' },
    { label: '👤 Usuários Free', path: '/admin/users', color: 'bg-gradient-to-r from-blue-500 to-blue-700 text-white' },
    { label: '🛡️ Moderação', path: '/admin/moderation', color: 'bg-[#4D1A4A]/50 border border-[#4D1A4A] text-white' },
    { label: '📋 Verificações KYC', path: '/admin/verifications', color: 'bg-[#C79F5E]/20 border border-[#C79F5E]/50 text-[#C79F5E]' },
    { label: '👨‍👩‍👧 Padrinhos/Madrinhas', path: '/admin/godparents', color: 'bg-pink-500/20 border border-pink-500/50 text-pink-400' },
    { label: '💌 Convites Sociais', path: '/admin/social-invites', color: 'bg-fuchsia-500/20 border border-fuchsia-500/50 text-fuchsia-300' },
    { label: '📝 Triagem Rede Social', path: '/admin/social-approvals', color: 'bg-yellow-500/20 border border-yellow-500/50 text-yellow-300' },
    { label: '🎮 Gamificação', path: '/leaderboard', color: 'bg-purple-500/20 border border-purple-500/50 text-purple-400' },
    { label: '💬 Suporte', path: '/admin/support', color: 'bg-green-500/20 border border-green-500/50 text-green-400' },
    { label: '📊 Analytics', path: '/admin/analytics', color: 'bg-blue-500/20 border border-blue-500/50 text-blue-400' },
    { label: '🎉 Banners & Eventos', path: '/admin/banners', color: 'bg-gradient-to-r from-pink-500 to-fuchsia-600 text-white' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#C79F5E] mx-auto mb-4"></div>
          <p className="text-gray-400">Carregando Sala de Controle...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white pb-10">
      <div className="max-w-7xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#C79F5E] flex items-center gap-3">
            🎛️ Sala de Controle
          </h1>
          <p className="text-gray-400 mt-2">Dashboard administrativo LYBERHOT</p>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-300 mb-4">⚡ Ações Rápidas</h2>
          <div className="flex flex-wrap gap-3">
            {quickActions.map((action, index) => (
              <button
                key={index}
                onClick={() => navigate(action.path)}
                className={`${action.color} px-4 py-2 rounded-full font-semibold text-sm transition-all hover:scale-105 hover:shadow-lg`}
              >
                {action.label}
              </button>
            ))}
          </div>
        </div>

        {/* KPI Cards Grid */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-300 mb-4">📊 KPIs Principais</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {kpiCards.map((card) => (
              <motion.div
                key={card.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={card.onClick}
                className={`relative cursor-pointer rounded-2xl p-5 bg-gradient-to-br ${card.color} shadow-xl overflow-hidden transition-all ${
                  activeCard === card.id ? 'ring-2 ring-white' : ''
                }`}
              >
                {/* Efeito de pulso para indicador online */}
                {card.pulse && (
                  <div className="absolute top-3 left-3">
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                    </span>
                  </div>
                )}
                <div className="absolute top-3 right-3 text-3xl opacity-30">{card.icon}</div>
                <div className="relative z-10">
                  <p className="text-white/80 text-xs font-medium mb-1">{card.title}</p>
                  <p className="text-2xl sm:text-3xl font-bold text-white mb-1">{card.value}</p>
                  <p className="text-white/60 text-xs">{card.subtitle}</p>
                </div>
                {activeCard === card.id && (
                  <div className="absolute bottom-2 right-2">
                    <span className="text-xs bg-white/20 px-2 py-1 rounded-full">Ver mais ↓</span>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Expanded Card Details */}
        <AnimatePresence>
          {activeCard && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-8"
            >
              {/* Receita Details */}
              {activeCard === 'receita' && financial?.recent_transactions && (
                <div className="bg-[#1A1A1A] border border-[#C79F5E]/30 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-[#C79F5E]">📜 Transações Recentes</h3>
                    <button onClick={() => setActiveCard(null)} className="text-gray-400 hover:text-white">✕</button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-3 px-4 text-gray-400 text-sm">Data</th>
                          <th className="text-left py-3 px-4 text-gray-400 text-sm">Usuário</th>
                          <th className="text-left py-3 px-4 text-gray-400 text-sm">Valor</th>
                          <th className="text-left py-3 px-4 text-gray-400 text-sm">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {financial.recent_transactions.slice(0, 10).map((tx, index) => (
                          <tr key={index} className="border-b border-white/5 hover:bg-white/5">
                            <td className="py-3 px-4 text-sm">{tx.created_at ? new Date(tx.created_at).toLocaleString('pt-BR') : 'N/A'}</td>
                            <td className="py-3 px-4 text-sm">{tx.user_name || 'N/A'}</td>
                            <td className="py-3 px-4 text-sm font-semibold text-green-400">R$ {tx.amount?.toFixed(2) || '0.00'}</td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded-full text-xs ${tx.status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                                {tx.status === 'paid' ? '✓ Pago' : '⏳ Pendente'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Top Criadores */}
              {activeCard === 'criadores' && financial?.top_creators && (
                <div className="bg-[#1A1A1A] border border-green-500/30 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-green-400">🏆 Top 10 Criadores por Receita</h3>
                    <button onClick={() => setActiveCard(null)} className="text-gray-400 hover:text-white">✕</button>
                  </div>
                  <div className="grid gap-3">
                    {financial.top_creators.map((creator, index) => (
                      <div key={creator.id} className="flex items-center justify-between p-4 bg-[#0D0D0D] border border-green-500/10 rounded-xl hover:border-green-500/30 transition-all cursor-pointer"
                        onClick={() => navigate(`/creator/${creator.id}`)}>
                        <div className="flex items-center gap-4">
                          <div className={`text-2xl font-bold ${index === 0 ? 'text-[#D4AF37]' : index === 1 ? 'text-gray-300' : index === 2 ? 'text-[#CD7F32]' : 'text-gray-500'}`}>
                            #{index + 1}
                          </div>
                          <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-[#4D1A4A] to-[#C79F5E] flex-shrink-0">
                            {creator.picture ? (
                              <img src={getImageUrl(creator.picture)} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-white font-bold">
                                {creator.name?.charAt(0) || '?'}
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="font-semibold">{creator.name}</div>
                            <div className="text-sm text-gray-400">{creator.subscribers} assinantes</div>
                          </div>
                        </div>
                        <div className="text-xl font-bold text-green-400">R$ {creator.revenue.toFixed(2)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Fundadores Stats */}
              {activeCard === 'fundadores' && (
                <div className="bg-[#1A1A1A] border border-[#D4AF37]/30 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-[#D4AF37]">⭐ Membros Fundadores</h3>
                    <button onClick={() => setActiveCard(null)} className="text-gray-400 hover:text-white">✕</button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="bg-[#0D0D0D] p-4 rounded-xl text-center">
                      <div className="text-3xl font-bold text-[#D4AF37]">{stats?.fundadores?.total || 0}</div>
                      <div className="text-sm text-gray-400">Total</div>
                    </div>
                    <div className="bg-[#0D0D0D] p-4 rounded-xl text-center">
                      <div className="text-3xl font-bold text-green-400">{stats?.fundadores?.ativos || 0}</div>
                      <div className="text-sm text-gray-400">Ativos</div>
                    </div>
                    <div className="bg-[#0D0D0D] p-4 rounded-xl text-center">
                      <div className="text-3xl font-bold text-blue-400">{stats?.fundadores?.indicados || 0}</div>
                      <div className="text-sm text-gray-400">Indicados</div>
                    </div>
                    <div className="bg-[#0D0D0D] p-4 rounded-xl text-center">
                      <div className="text-3xl font-bold text-purple-400">R$ {stats?.fundadores?.comissoes?.toFixed(2) || '0.00'}</div>
                      <div className="text-sm text-gray-400">Comissões</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Posts Stats */}
              {activeCard === 'posts' && (
                <div className="bg-[#1A1A1A] border border-pink-500/30 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-pink-400">📱 Estatísticas de Conteúdo</h3>
                    <button onClick={() => setActiveCard(null)} className="text-gray-400 hover:text-white">✕</button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="bg-[#0D0D0D] p-4 rounded-xl text-center">
                      <div className="text-3xl font-bold text-pink-400">{stats?.content?.posts || 0}</div>
                      <div className="text-sm text-gray-400">Posts Total</div>
                    </div>
                    <div className="bg-[#0D0D0D] p-4 rounded-xl text-center">
                      <div className="text-3xl font-bold text-green-400">{stats?.content?.today || 0}</div>
                      <div className="text-sm text-gray-400">Hoje</div>
                    </div>
                    <div className="bg-[#0D0D0D] p-4 rounded-xl text-center">
                      <div className="text-3xl font-bold text-blue-400">{stats?.content?.premium || 0}</div>
                      <div className="text-sm text-gray-400">Premium</div>
                    </div>
                    <div className="bg-[#0D0D0D] p-4 rounded-xl text-center">
                      <div className="text-3xl font-bold text-yellow-400">{stats?.content?.stories || 0}</div>
                      <div className="text-sm text-gray-400">Stories</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Plataforma Revenue Breakdown */}
              {activeCard === 'plataforma' && (
                <div className="bg-[#1A1A1A] border border-[#4D1A4A]/50 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-[#6B2D5C]">🏢 Detalhamento Receita Plataforma</h3>
                    <button onClick={() => setActiveCard(null)} className="text-gray-400 hover:text-white">✕</button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-[#0D0D0D] p-4 rounded-xl">
                      <div className="text-sm text-gray-400 mb-1">Assinaturas</div>
                      <div className="text-2xl font-bold text-[#C79F5E]">R$ {(financial?.summary?.subscriptions_revenue || 0).toFixed(2)}</div>
                    </div>
                    <div className="bg-[#0D0D0D] p-4 rounded-xl">
                      <div className="text-sm text-gray-400 mb-1">Presentes</div>
                      <div className="text-2xl font-bold text-pink-400">R$ {(financial?.summary?.gifts_revenue || 0).toFixed(2)}</div>
                    </div>
                    <div className="bg-[#0D0D0D] p-4 rounded-xl">
                      <div className="text-sm text-gray-400 mb-1">Gorjetas</div>
                      <div className="text-2xl font-bold text-green-400">R$ {(financial?.summary?.tips_revenue || 0).toFixed(2)}</div>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AdminDashboard;
